import React, { ButtonHTMLAttributes, forwardRef, ElementType } from 'react';
import { classNames } from '@/utils/classNames';

interface BaseButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: React.ReactNode;
  as?: ElementType;
}

interface ButtonProps
  extends BaseButtonProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> {
  [key: string]: unknown;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      className,
      children,
      as: Component = 'button' as ElementType,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed border border-transparent';

    // 統一されたスタイリングシステム（Tailwind + フォールバック）
    const variantClasses = {
      primary: [
        // Tailwind classes (important: true で自動!important)
        'bg-blue-600 text-white border-blue-600',
        'hover:bg-blue-700',
        'focus:ring-blue-500',
        'dark:bg-blue-500 dark:hover:bg-blue-600 dark:border-blue-500',
        'disabled:bg-gray-500 disabled:text-gray-300 disabled:border-gray-500',
        'dark:disabled:bg-gray-500 dark:disabled:text-gray-400 dark:disabled:border-gray-500',
        // トランジション追加
        'transition-colors duration-200',
      ].join(' '),
      secondary: [
        'bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-500',
        'dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600',
        'border-gray-300 dark:border-gray-600',
        'disabled:bg-gray-300 disabled:text-gray-500',
        'dark:disabled:bg-gray-600 dark:disabled:text-gray-400',
      ].join(' '),
      danger: [
        'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
        'dark:bg-red-600 dark:hover:bg-red-700 border-red-600 dark:border-red-600',
        'disabled:bg-gray-500 disabled:text-gray-300',
        'dark:disabled:bg-gray-500 dark:disabled:text-gray-400',
      ].join(' '),
      ghost: [
        'text-gray-700 hover:bg-gray-100 focus:ring-gray-500',
        'dark:text-gray-300 dark:hover:bg-gray-700',
        'border-transparent hover:border-gray-300 dark:hover:border-gray-600',
        'disabled:text-gray-400 disabled:bg-transparent',
        'dark:disabled:text-gray-600',
      ].join(' '),
    };

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm rounded-md',
      md: 'px-4 py-2 text-sm rounded-md',
      lg: 'px-6 py-3 text-base rounded-lg',
    };

    // CSSカスタムプロパティによるフォールバック（primaryの場合）
    const inlineStyle =
      variant === 'primary'
        ? {
            backgroundColor: disabled
              ? 'var(--color-gray-500, #6b7280)'
              : 'var(--color-blue-600, #2563eb)',
            borderColor: disabled
              ? 'var(--color-gray-500, #6b7280)'
              : 'var(--color-blue-600, #2563eb)',
            color: disabled ? 'var(--color-gray-300, #d1d5db)' : 'var(--color-white, white)',
          }
        : {};

    const ElementType = Component as ElementType;

    return (
      <ElementType
        ref={ref}
        disabled={disabled || loading}
        className={classNames(
          baseClasses,
          variant === 'primary'
            ? 'bg-[var(--btn-bg)] border-[var(--btn-border)] text-[var(--btn-color)]'
            : variantClasses[variant as keyof typeof variantClasses],
          sizeClasses[size as keyof typeof sizeClasses],
          className as string
        )}
        style={inlineStyle}
        {...props}
      >
        {loading && (
          <svg className='animate-spin -ml-1 mr-2 h-4 w-4' fill='none' viewBox='0 0 24 24'>
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            ></circle>
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            ></path>
          </svg>
        )}
        {children}
      </ElementType>
    );
  }
);

Button.displayName = 'Button';

export default Button;
// Test change
